/**
 * Businesses WRITE endpoints — request schemas (Pattern B+,
 * `apps/api/src/routes/people/businesses.ts`). Mirrors `people.ts`.
 *
 * `createBusinessSchema` / `updateBusinessSchema` ARE THE FIELD WHITELIST for
 * the `create_business(p_organization_id, p_payload)` and
 * `update_business(p_business_id, p_payload)` INVOKER RPCs
 * (`20260925210000_business_backend_writes.sql`). The route hands the parsed
 * body to the RPC as `p_payload` UNCHANGED, so the keys below are exactly the
 * payload keys the RPC reads (`BUSINESS_SCALAR_KEYS` + `vendor_identity`, and
 * `BUSINESS_LOGO_KEYS` on update only). Every object here is `.strict()`: zod
 * silently STRIPS unknown keys otherwise, so a body carrying
 * `organization_id` or `created_by` would 200 with the key dropped instead of
 * 400ing, and the whitelist would be untestable.
 *
 * NEVER accepted (the RPC ignores them too, but the schema is the first
 * line): `organization_id` (the `:orgId` route param), `created_by`
 * (`auth.uid()` in the RPC), `primary_contact_id`, `billing_contact_id`,
 * `created_at`, `updated_at`.
 *
 * CODE. On create `code` is optional: blank / absent → `null`, and the
 * BEFORE INSERT trigger (`auto_generate_vendor_code` →
 * `entity_code_config('businesses')`) generates `VEN-###`. The API retries a
 * `unique_business_code_per_org` collision only when the caller left the code
 * blank; a typed duplicate is 409 `business_code_taken`. On update `code` is
 * NOT NULL, so an empty / whitespace code is REJECTED here — only an ABSENT
 * key means "leave it alone".
 *
 * LOGOS. `logo_small` / `logo_medium` / `logo_large` are accepted by UPDATE
 * only (the upload runs after the business exists, and `useImageDelete`
 * clears them through the same PATCH with JSON `null`).
 *
 * VENDOR IDENTITY. The optional `vendor_identity` object is upserted in the
 * SAME transaction as the business. Its keys are presence-keyed inside the
 * RPC: an absent key leaves the stored column alone, JSON `null` clears it.
 * An absent `vendor_identity` leaves the identity row untouched.
 *
 * EMPTY STRINGS: every optional text field folds `''` (and whitespace) to
 * `null`, so a form that sends a cleared input clears the column rather than
 * storing `''`. Required text must be non-empty after trimming.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------
/** `vendors_availability_status_check`. */
export const BUSINESS_AVAILABILITY_STATUSES = ["available", "busy", "unavailable"];
/** `vendor_locations_location_type_check`. The RPC defaults an absent type to `'headquarters'`. */
export const BUSINESS_LOCATION_TYPES = [
    "headquarters",
    "billing",
    "shipping",
    "branch",
    "other",
];
// ---------------------------------------------------------------------------
// Field helpers (module-private twins of the ones in people.ts)
// ---------------------------------------------------------------------------
/** `''` / whitespace-only → `null`; anything else passes through untouched. */
const blankToNull = (value) => typeof value === "string" && value.trim() === "" ? null : value;
/** Optional, nullable free text; `''` clears the column. */
const optionalText = (max) => z.preprocess(blankToNull, z.string().trim().max(max).nullable()).optional();
/** Optional, nullable email; `''` clears the column. */
const optionalEmail = z
    .preprocess(blankToNull, z.string().trim().email().max(320).nullable())
    .optional();
/** Optional, nullable uuid; `''` clears the column. */
const optionalUuid = z.preprocess(blankToNull, z.string().uuid().nullable()).optional();
/** A short vocabulary token (type / specialty). */
const token = z.string().trim().min(1).max(100);
const requiredName = z.string().trim().min(1).max(255);
/** A business code as typed by a user (`unique_business_code_per_org`). */
const businessCode = z.string().trim().min(1).max(50);
const AT_LEAST_ONE_FIELD = "At least one field must be provided";
/** superRefine for PATCH bodies: an empty object is a 400, not a no-op 200. */
function refineNonEmpty(value, ctx) {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: AT_LEAST_ONE_FIELD });
    }
}
// ---------------------------------------------------------------------------
// Vendor identity (nested in create / update)
// ---------------------------------------------------------------------------
/** The `vendor_identity` keys `_business_upsert_vendor_identity` reads. */
export const BUSINESS_VENDOR_IDENTITY_KEYS = [
    "trading_name",
    "legal_form_id",
    "registration_number",
    "vat_number",
    "tax_entity_number",
    "country_code",
    "iban",
    "bic",
    "is_related_party",
];
/**
 * The `business_vendor_identity` row a business create / update upserts.
 * Presence-keyed (see the file header). `legal_form_id` is a GLOBAL
 * vocabulary (`legal_forms`), so the route checks it exists — not that it is
 * in the org. `country_code` is folded to upper case and must match
 * `business_vendor_identity_country_code_check` (`^[A-Z]{2}$`).
 */
export const businessVendorIdentitySchema = z
    .object({
    trading_name: optionalText(255),
    legal_form_id: optionalUuid,
    registration_number: optionalText(100),
    vat_number: optionalText(100),
    tax_entity_number: optionalText(100),
    country_code: z
        .preprocess(blankToNull, z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, "Must be a two-letter country code")
        .nullable())
        .optional(),
    iban: optionalText(64),
    bic: optionalText(20),
    is_related_party: z.boolean().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Business create / update
// ---------------------------------------------------------------------------
/** The scalar `p_payload` keys both RPCs read. */
export const BUSINESS_SCALAR_KEYS = [
    "name",
    "code",
    "contact_email",
    "contact_phone",
    "address",
    "business_type_id",
    "artist_specialties",
    "availability_status",
];
/** The logo keys only `update_business` is sent (the RPCs both read them). */
export const BUSINESS_LOGO_KEYS = ["logo_small", "logo_medium", "logo_large"];
/** The nested object key both RPCs read. */
export const BUSINESS_VENDOR_IDENTITY_KEY = "vendor_identity";
/** Scalars shared by create and update (all but `name`, `code`, `business_type_id`). */
const businessOptionalScalars = {
    contact_email: optionalEmail,
    contact_phone: optionalText(50),
    address: optionalText(1000),
    artist_specialties: z.array(token).max(50).nullable().optional(),
    availability_status: z
        .preprocess(blankToNull, z.enum(BUSINESS_AVAILABILITY_STATUSES).nullable())
        .optional(),
};
const businessLogoFields = {
    logo_small: optionalText(2048),
    logo_medium: optionalText(2048),
    logo_large: optionalText(2048),
};
/**
 * POST /orgs/:orgId/businesses body. `name` and `business_type_id` are the
 * NOT NULL columns the caller owns (the type must be global or the org's —
 * the route pre-checks it, the RPC re-checks). `code` blank / absent →
 * `null` → `VEN-###`.
 */
export const createBusinessSchema = z
    .object({
    name: requiredName,
    code: z.preprocess(blankToNull, businessCode.nullable()).optional(),
    business_type_id: z.string().uuid(),
    ...businessOptionalScalars,
    vendor_identity: businessVendorIdentitySchema.optional(),
})
    .strict();
/**
 * PATCH /orgs/:orgId/businesses/:businessId body — any subset, at least one
 * key. A present key is written (JSON `null` clears a nullable column); an
 * absent key is left alone. `name`, `code` and `business_type_id` are NOT
 * NULL columns: present means a real value (a blank `code` is a 400, never
 * "regenerate").
 */
export const updateBusinessSchema = z
    .object({
    name: requiredName.optional(),
    code: businessCode.optional(),
    business_type_id: z.string().uuid().optional(),
    ...businessOptionalScalars,
    ...businessLogoFields,
    vendor_identity: businessVendorIdentitySchema.optional(),
})
    .strict()
    .superRefine(refineNonEmpty);
// ---------------------------------------------------------------------------
// Addresses (upsert_business_location)
//   POST  …/businesses/:businessId/locations
//   PATCH …/businesses/:businessId/locations/:locationId
// ---------------------------------------------------------------------------
/** The `p_payload` keys `upsert_business_location` reads. */
export const BUSINESS_LOCATION_KEYS = [
    "gis_location_id",
    "location_type",
    "is_primary",
    "is_active",
    "notes",
];
const businessLocationOptionalFields = {
    location_type: z.enum(BUSINESS_LOCATION_TYPES).optional(),
    is_primary: z.boolean().optional(),
    is_active: z.boolean().optional(),
    notes: optionalText(5000),
};
/**
 * POST body. `gis_location_id` is REQUIRED (`business_locations.gis_location_id`
 * is a nullable column, so the schema and the RPC enforce it) and must be one
 * of the org's `gis_locations` (route pre-check). The first address of a
 * business is always primary; `is_primary: true` clears the others in the
 * same transaction.
 */
export const businessLocationInputSchema = z
    .object({
    gis_location_id: z.string().uuid(),
    ...businessLocationOptionalFields,
})
    .strict();
/**
 * PATCH body — any subset, at least one key. Set-primary is this PATCH with
 * `{ is_primary: true }`. `gis_location_id`, when present, is a real id.
 */
export const businessLocationUpdateSchema = z
    .object({
    gis_location_id: z.string().uuid().optional(),
    ...businessLocationOptionalFields,
})
    .strict()
    .superRefine(refineNonEmpty);
// ---------------------------------------------------------------------------
// Contacts (upsert_business_contact)
//   POST  …/businesses/:businessId/contacts
//   PATCH …/businesses/:businessId/contacts/:contactId
// ---------------------------------------------------------------------------
/** The `p_payload` keys `upsert_business_contact` reads. */
export const BUSINESS_CONTACT_KEYS = ["person_id", "contact_type", "is_primary", "notes"];
/**
 * POST body. `person_id` (a nullable column, required here and in the RPC)
 * must be one of the org's people (route pre-check); `contact_type` is free
 * text in the DB (the web offers `BUSINESS_CONTACT_TYPES`). One row per
 * (business, person): a second is 409 `business_contact_exists`.
 */
export const businessContactInputSchema = z
    .object({
    person_id: z.string().uuid(),
    contact_type: token,
    is_primary: z.boolean().optional(),
    notes: optionalText(5000),
})
    .strict();
/** PATCH body — any subset, at least one key. */
export const businessContactUpdateSchema = z
    .object({
    person_id: z.string().uuid().optional(),
    contact_type: token.optional(),
    is_primary: z.boolean().optional(),
    notes: optionalText(5000),
})
    .strict()
    .superRefine(refineNonEmpty);
// ---------------------------------------------------------------------------
// Contracts (POST …/businesses/:businessId/contracts — a plain insert)
// ---------------------------------------------------------------------------
/** The `business_contracts` columns the caller may set. */
export const BUSINESS_CONTRACT_KEYS = [
    "contract_number",
    "contract_name",
    "contract_type",
    "project_id",
    "location_id",
    "start_date",
    "end_date",
    "renewal_date",
    "auto_renewal",
    "contract_value",
    "currency",
    "payment_terms",
    "notice_period_days",
    "key_terms",
];
/**
 * POST body. The server pins `organization_id`, `business_id` (route param),
 * `created_by` and `status: 'active'` — none of them, nor `file_id` /
 * `metadata`, is accepted. `project_id` must be the org's project;
 * `location_id` is the org's LEGACY `locations` row (FK
 * `fk_vendor_contracts_location`), not a place or gis location — both route
 * pre-checks. Dates are calendar days (`yyyy-MM-dd`). `key_terms` keeps the
 * stored `{ terms }` jsonb shape. Absent `currency` / `notice_period_days` /
 * `auto_renewal` / `contract_type` take the column defaults.
 */
export const createBusinessContractSchema = z
    .object({
    contract_number: z.string().trim().min(1).max(100),
    contract_name: requiredName,
    contract_type: token.optional(),
    project_id: optionalUuid,
    location_id: optionalUuid,
    start_date: calendarDateSchema,
    end_date: z.preprocess(blankToNull, calendarDateSchema.nullable()).optional(),
    renewal_date: z.preprocess(blankToNull, calendarDateSchema.nullable()).optional(),
    auto_renewal: z.boolean().optional(),
    contract_value: z.number().finite().nonnegative().nullable().optional(),
    currency: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{3}$/, "Must be a three-letter currency code")
        .optional(),
    payment_terms: optionalText(1000),
    notice_period_days: z.number().int().min(0).max(3650).optional(),
    key_terms: z
        .object({ terms: z.string().trim().max(20000).optional() })
        .strict()
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Coded error envelopes (the web reads `err.envelope?.code`)
// ---------------------------------------------------------------------------
export const BUSINESS_ERROR_CODES = {
    /** 409 — `unique_business_code_per_org`: a typed code another business already uses. */
    codeTaken: "business_code_taken",
    /** 409 — DELETE refused: something references the business (`reasons` says what). */
    inUse: "business_in_use",
    /** 409 — DELETE of the primary address while the business has others; set another primary first. */
    locationPrimary: "business_location_primary",
    /** 409 — `unique_vendor_person`: the person is already a contact of this business. */
    contactExists: "business_contact_exists",
    /** 409 — `vendor_locations_vendor_id_gis_location_id_location_type_key`: the same address with the same type. */
    locationExists: "business_location_exists",
    /**
     * 409 — `uq_business_locations_primary` / `uq_business_contacts_primary`:
     * a concurrent primary flip won the race. Safe to retry.
     */
    primaryConflict: "business_primary_conflict",
};
//# sourceMappingURL=businesses.js.map